import React, { useState } from "react";
import { Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "./api/axios";

export default function ForgotPassword() {
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");

    // Validate WhatsApp number
    if (!whatsappNumber.trim()) {
      setMessage("Nomor WhatsApp tidak boleh kosong");
      setIsLoading(false);
      return;
    }

    // Basic phone number validation
    const phoneRegex = /^(62|0)[0-9]{9,15}$/;
    if (!phoneRegex.test(whatsappNumber)) {
      setMessage("Format nomor WhatsApp tidak valid");
      setIsLoading(false);
      return;
    }

    try {
      // API call to send reset link via WhatsApp
      // Replace with your actual API endpoint
      const response = await api(`/send-verification-code?phone=${encodeURIComponent(whatsappNumber)}`, {
        method: "GET",
      });
      // console.log(`/send-verification-code?phone=${encodeURIComponent(whatsappNumber)}`);
      if (response.status === 200 && response.data?.message === "verification code sent") {
        setMessage("Link reset password telah dikirim ke WhatsApp Anda");
      } else {
        setMessage("Terjadi kesalahan. Silakan coba lagi.");
        console.log("Error response:", response);
      }
    } catch (error) {
      setMessage("Terjadi kesalahan koneksi. Silakan coba lagi.");
    } finally {
      setIsLoading(false);
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove non-numeric characters
    const numbers = value.replace(/\D/g, "");

    // Format with country code if needed
    if (numbers.startsWith("8")) {
      return "62" + numbers;
    } else if (numbers.startsWith("08")) {
      return "62" + numbers.substring(1);
    } else if (numbers.startsWith("628")) {
      return numbers;
    }

    return numbers;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setWhatsappNumber(formatted);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header with back button */}
      <div className="flex items-center justify-between px-4 py-4 pt-8">
        <button
          onClick={() => navigate("/login")}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          disabled={isLoading}
        >
          <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
        </button>
        <div className="flex-1" />
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto pb-32">
        <div className="flex flex-col items-center px-8 py-4">
          <div className="mb-6 text-left w-full max-w-md">
            <img src="/logohallostroke.png" alt="Logo" className="h-12 mb-8" />
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Lupa Password
            </h1>
            <p className="text-gray-600 text-sm mb-8">
              Masukkan nomor WhatsApp Anda untuk mendapatkan link reset password
            </p>
          </div>

          <div className="w-full max-w-md">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Nomor WhatsApp<span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    value={whatsappNumber}
                    onChange={handlePhoneChange}
                    placeholder="62812xxxxxxxx"
                    className="mt-1 w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    required
                    disabled={isLoading}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Contoh: 6281234567890 atau 081234567890
                </p>
              </div>

              {message && (
                <div className="mt-4 mb-4">
                  <div className={`border rounded-lg p-4 ${
                    message.includes("dikirim")
                      ? "bg-green-50 border-green-200"
                      : "bg-red-50 border-red-200"
                  }`}>
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg
                          className={`h-5 w-5 ${
                            message.includes("dikirim") ? "text-green-400" : "text-red-400"
                          }`}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          {message.includes("dikirim") ? (
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          ) : (
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          )}
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className={`text-sm font-medium ${
                          message.includes("dikirim") ? "text-green-800" : "text-red-800"
                        }`}>
                          {message}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-xs text-right mt-4">
                <span className="text-red-500">*</span>
                <span className="font-semibold text-black">wajib diisi</span>
              </p>
            </form>
          </div>

          {/* Add extra space at bottom for better scrolling */}
          <div className="h-16"></div>
        </div>
      </div>

      {/* Fixed bottom buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white px-8 pb-6 pt-4 shadow-lg border-t border-gray-100">
        <div className="w-full max-w-md mx-auto">
          <button
            onClick={handleSubmit}
            type="button"
            className="w-full bg-blue-500 text-white py-2 rounded-md hover:bg-blue-600 transition mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? "Mengirim..." : "Kirim Link Reset"}
          </button>

          <div className="flex items-center justify-between my-2">
            <hr className="w-full border-gray-300" />
            <span className="mx-2 text-gray-500 text-sm">atau</span>
            <hr className="w-full border-gray-300" />
          </div>

          <button
            onClick={() => navigate("/login")}
            className="w-full border border-blue-500 text-blue-500 py-2 rounded-md text-base font-medium hover:bg-blue-50 transition mt-3 disabled:opacity-50"
            disabled={isLoading}
          >
            Kembali ke Login
          </button>
        </div>
      </div>
    </div>
  );
}